import { Component, effect, output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators, } from '@angular/forms';
import { LeadFilterService } from '../../lead-filter.service';
import { TranslatePipe } from '@ngx-translate/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, } from '@angular/material/expansion';
import { MatOption, MatSelect } from '@angular/material/select';

import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { BusinessCategory, priorities, Priority } from '../../../lead.model';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'app-all-leads-filter',
  imports: [
    TranslatePipe,
    FormsModule,
    MatLabel,
    MatExpansionPanelTitle,
    MatExpansionPanelHeader,
    MatExpansionPanel,
    MatFormField,
    MatError,
    MatSelect,
    MatOption,
    ReactiveFormsModule,
    MatInput,
    MatIcon,
    MatIconButton,
    MatCheckbox,
  ],
  templateUrl: './all-leads-filter.component.html',
  styleUrl: './all-leads-filter.component.scss',
})
export class AllLeadsFilterComponent {
  sortChange = output<boolean>();

  protected errorMessage = signal('');

  protected plzPrefixControl: FormControl<string | null>;

  protected startExpanded: boolean;

  constructor(protected filterService: LeadFilterService) {
    this.startExpanded = filterService.detailedFiltersActive();
    this.plzPrefixControl = new FormControl(
      filterService.postalCodePrefix().join(', '),
      Validators.pattern('^[0-9]{0,5}([ ,;]+[0-9]{0,5})*$'),
    );
    this.plzPrefixControl.valueChanges.subscribe((value) => {
      if (this.plzPrefixControl.hasError('pattern')) {
        this.errorMessage.set(
          'components.all-leads-list.input-postalcode-error',
        );
      } else {
        this.errorMessage.set('');
        this.filterService.postalCodePrefix.set(
          this.splitPostalCodePrefixes(value),
        );
      }
    });
    effect(() => {
      if (!this.filterService.detailedFiltersActive()) {
        this.resetStreetAndDistrictFilters();
      }
    });
  }

  private splitPostalCodePrefixes(value: string | null | undefined): string[] {
    if (!value) return [];
    return value
      .split(/[ ,;]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  protected selectPriority(priority: Priority | undefined) {
    this.filterService.priority.set(priority);
  }

  protected selectCity(cities: string[]) {
    this.filterService.cityFilter.set(cities);
  }

  protected selectStreet(streets: string[]) {
    this.filterService.streetFilter.set(streets);
  }

  protected selectDistricts(districts: string[]) {
    this.filterService.districtFilter.set(districts);
  }

  protected selectDaysToDueDate(value: string | number | null | undefined) {
    if (value === null || value === undefined || value === '') {
      this.filterService.daysToDueDate.set(undefined);
      return;
    }

    const parsedValue = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(parsedValue) && parsedValue >= 0) {
      this.filterService.daysToDueDate.set(parsedValue);
    }
  }

  protected selectBusinessCategory(value: BusinessCategory | undefined) {
    this.filterService.businessCategory.set(value);
  }

  protected selectLeadType(leadTypes: string[]) {
    this.filterService.leadType.set(leadTypes);
  }

  protected onSortChange(value: boolean): void {
    this.sortChange.emit(value);
  }

  private resetStreetAndDistrictFilters() {
    this.filterService.streetFilter.set([]);
    this.filterService.districtFilter.set([]);
  }

  protected clearPriorityFilter() {
    this.filterService.priority.set(undefined);
  }

  protected clearCityFilter() {
    this.filterService.priority.set(undefined);
    this.filterService.cityFilter.set([]);
  }

  protected clearStreetFilter() {
    this.filterService.streetFilter.set([]);
  }

  protected clearDistrictFilter() {
    this.filterService.districtFilter.set([]);
  }

  protected clearDaysToDueDateFilter() {
    this.filterService.daysToDueDate.set(undefined);
  }

  protected clearBusinessCategoryFilter() {
    this.filterService.businessCategory.set(undefined);
  }

  protected clearLeadTypeFilter() {
    this.filterService.leadType.set([]);
  }

  protected readonly priorities = priorities;
}
